import * as api from '@api/auth/api-function';
import { EQUERY_KEY } from '@constant/moashelf/query-key';
import { EService } from '@datatype/type';
import { useApiQuery, useApiMutation } from '@hook/common/useApi';
import { useTypedSelector } from '@hook/common/useStore';
import { LOCAL_STORAGE_USER_KEY } from '@config/auth';
import { QueryClient, useMutation, UseMutationOptions } from '@tanstack/react-query';
import { IApiResponse } from '@datatype/common/api-type';
import {
  IAuthenticateCodeRequest,
  IIssueCodeRequest,
  IIssueCodeData,
  IModifyMeRequest,
  ISigninData,
  IInhouseSignupRequest,
  IInhouseSignInRequest,
  ISignupData,
  IUserData,
  IOAuthSignupRequest,
  IRepasswordRequest,
  IOAuthPageData,
  IGetOAuthPageRequest,
} from '@datatype/auth/api-type';

/**
 * ###################################
 * @description auth query
 * ###################################
 */

/**
 * @description my 정보 조회 query 함수
 * 비로그인(게스트) 상태에서는 호출하지 않음 → 불필요한 /auth/me 401 차단
 */
export const useFindMeQuery = () => {
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  // userKey를 queryKey에 포함해 사용자 전환 시 캐시 격리
  const userKey = localStorage.getItem(LOCAL_STORAGE_USER_KEY) ?? '';
  return useApiQuery([EQUERY_KEY.MY_INFO, userKey], api.findMe, {}, { enabled: isAuthenticated });
};

/**
 * @description 나의 user name 목록 조회 query 함수
 */
export const useFindMyUsersQuery = (service: EService, email: string) => {
  return useApiQuery([EQUERY_KEY.MY_USERS, email], api.findMyUsers, { service, email }, { enabled: false });
};

/**
 * ###################################
 * @description auth mutation
 * ###################################
 * auth mutation은 콜백관리하기가 어려우므로, onSuccess를 사용하지 않음 (mutateAsync 활용하여, succes 처리)
 */

/**
 * @description 회원가입 mutation 함수
 */
export const useIssueCodeMutation = () => {
  return useApiMutation<IApiResponse<IIssueCodeData>, IIssueCodeRequest>(api.issueCodeApi, {});
};

export const useAuthenticateCodeMutation = () => {
  return useApiMutation<IApiResponse<void>, IAuthenticateCodeRequest>(api.authenticateCodeApi, {});
};

/**
 * @description 회원가입 mutation 함수
 */
export const useInhouseSignupMutation = () => {
  return useApiMutation<IApiResponse<ISignupData>, IInhouseSignupRequest>(api.inhouseSignupApi, {});
};

/**
 * @description 로그인 mutation 함수
 */
export const useInhouseSigninMutation = () => {
  return useApiMutation<IApiResponse<ISigninData>, IInhouseSignInRequest>(api.inhouseSigninApi, {});
};

/**
 * @description oauth sign page in mutation 함수
 */
export const useOpenOAuthSignInPageMutation = () => {
  return useApiMutation<IApiResponse<IOAuthPageData>, IGetOAuthPageRequest>(api.getOAuthPage, {});
};

/**
 * @description 나의 정보 수정 mutation 함수
 */
export const useRepasswordMutation = () => {
  return useApiMutation<IApiResponse<void>, IRepasswordRequest>(api.repassword, {});
};

/**
 * @description oauth sign page in mutation 함수
 */
export const useOAuthSignupMutation = () => {
  return useApiMutation<IApiResponse<ISignupData>, IOAuthSignupRequest>(api.oauthSignUp, {});
};

/**
 * @description 나의 정보 수정 mutation 함수
 */
export const useModifyMeMutation = () => {
  return useApiMutation<IApiResponse<IUserData>, IModifyMeRequest>(api.modifyMe, {});
};

/**
 * @description 회원탈퇴 mutation 함수
 */
export const useRemoveMeMutation = () => {
  return useApiMutation<IApiResponse<Pick<IUserData, 'key'>>, void>(api.removeMe, {});
};

/**
 * @description 로그아웃 mutation 함수
 */
export const useSignoutMutation = () => {
  return useMutation<IApiResponse<void>, void>({
    mutationFn: api.signOutApi,
  });
};
